import React, { useState, useMemo, useEffect, useRef } from 'react';
import { SortingState, VisibilityState, RowSelectionState } from '@tanstack/react-table';
import { DensityMode, FilterRule } from '../../../types/grid';
import { getGridSettings, saveGridSettings } from '../../../utils/gridPersistence';
import { DataTableProps } from './types';

export function useDataTableState<T extends Record<string, any>>({
  data,
  columns,
  serverSide,
  totalRows: controlledTotalRows,
  page: controlledPage,
  pageSize: controlledPageSize,
  onPageChange,
  onPageSizeChange,
  searchValue,
  onServerSearchChange,
  onServerSortChange,
  onServerFilterChange,
  initialFilterRules,
  initialSorting,
  initialSortField,
  initialSortDir,
  selectedRowIds: controlledSelectedIds,
  onSelectionChange,
  getRowId = (row: T, idx: number) => (row.id !== undefined ? String(row.id) : String(idx)),
  canEdit = false,
  onEditingChange,
  tableName = 'data',
  persistenceKey,
  onExportSelected,
  onExportAll,
}: DataTableProps<T>) {
  // Density state: 'compact' (34px) | 'normal' (42px)
  const [density, setDensity] = useState<DensityMode>(() => {
    try {
      const saved = localStorage.getItem('ericius_grid_density');
      if (saved === 'compact') return 'compact';
      return 'normal';
    } catch {
      return 'normal';
    }
  });

  const handleDensityChange = (newDensity: DensityMode) => {
    setDensity(newDensity);
    try {
      localStorage.setItem('ericius_grid_density', newDensity);
      localStorage.setItem('ericius_datatable_density', newDensity);
    } catch {
      // ignore
    }
  };

  const rowHeightClass =
    {
      compact: 'h-[34px]',
      normal: 'h-[42px]',
    }[density] || 'h-[42px]';

  // Server-side mode check: true if explicitly serverSide or totalRows provided
  const isServerSide = serverSide !== undefined ? serverSide : controlledTotalRows !== undefined;

  // Local pagination state if uncontrolled
  const [localPage, setLocalPage] = useState(1);
  const [localPageSize, setLocalPageSize] = useState(25);

  const page = controlledPage ?? localPage;
  const pageSize = controlledPageSize ?? localPageSize;

  // Mobile viewport detection for responsive pinned columns and drawer layout
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    return typeof window !== 'undefined' ? window.innerWidth < 768 : false;
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handlePageChange = (p: number) => {
    if (onPageChange) onPageChange(p);
    else setLocalPage(p);
  };

  const handlePageSizeChange = (s: number) => {
    if (onPageSizeChange) onPageSizeChange(s);
    else setLocalPageSize(s);
  };

  const resolvedPersistenceKey = persistenceKey || (tableName ? `screen_${tableName}` : undefined);

  // Search & Filter state
  const [globalFilter, setGlobalFilter] = useState(searchValue ?? '');

  // Keep globalFilter in sync if controlled searchValue changes
  useEffect(() => {
    if (searchValue !== undefined && searchValue !== globalFilter) {
      setGlobalFilter(searchValue);
    }
  }, [searchValue]);

  const [activeFilterRules, setActiveFilterRules] = useState<FilterRule[]>(() => {
    if (initialFilterRules !== undefined && initialFilterRules.length > 0) {
      return initialFilterRules;
    }
    if (resolvedPersistenceKey) {
      const saved = getGridSettings(resolvedPersistenceKey);
      if (saved?.filterRules && saved.filterRules.length > 0) {
        return saved.filterRules;
      }
    }
    return initialFilterRules || [];
  });

  // Sync activeFilterRules when initialFilterRules changes
  useEffect(() => {
    if (initialFilterRules !== undefined) {
      setActiveFilterRules((prev) => {
        if (JSON.stringify(prev) === JSON.stringify(initialFilterRules)) {
          return prev;
        }
        return initialFilterRules;
      });
    }
  }, [initialFilterRules]);

  const [sorting, setSorting] = useState<SortingState>(() => {
    if (initialSorting && initialSorting.length > 0) {
      return initialSorting;
    }
    if (initialSortField) {
      return [{ id: initialSortField, desc: (initialSortDir || 'desc') === 'desc' }];
    }
    if (resolvedPersistenceKey) {
      const saved = getGridSettings(resolvedPersistenceKey);
      if (saved?.sortRules && saved.sortRules.length > 0) {
        return saved.sortRules;
      }
      if (saved?.sortCol) {
        return [{ id: saved.sortCol, desc: (saved.sortDir || 'desc') === 'desc' }];
      }
    }
    return [];
  });
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  // Sync sorting when initialSorting, initialSortField, or initialSortDir changes
  useEffect(() => {
    if (initialSorting && initialSorting.length > 0) {
      setSorting((prev) => {
        const isSame =
          prev.length === initialSorting.length &&
          prev.every((s, i) => s.id === initialSorting[i].id && s.desc === initialSorting[i].desc);
        return isSame ? prev : initialSorting;
      });
    } else if (initialSortField) {
      const isDesc = (initialSortDir || 'desc') === 'desc';
      setSorting((prev) => {
        if (prev.length === 1 && prev[0].id === initialSortField && prev[0].desc === isDesc) {
          return prev;
        }
        return [{ id: initialSortField, desc: isDesc }];
      });
    }
  }, [initialSorting, initialSortField, initialSortDir]);

  // Export dropdown states
  const [exportOpen, setExportOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const exportButtonRef = useRef<HTMLButtonElement>(null);

  // Active cell & editing state
  const [activeCell, setActiveCell] = useState<{ rowId: string; colId: string } | null>(null);
  const [editingCell, setEditingCell] = useState<{ rowId: string; colId: string } | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Notify parent on inline cell editing change
  useEffect(() => {
    onEditingChange?.(editingCell !== null);
  }, [editingCell, onEditingChange]);

  // Keyboard shortcut '/' to focus search
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.key === '/' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const activeTag = (document.activeElement?.tagName || '').toLowerCase();
        const isEditable =
          activeTag === 'input' ||
          activeTag === 'textarea' ||
          (document.activeElement as HTMLElement)?.isContentEditable;
        if (!isEditable) {
          e.preventDefault();
          searchInputRef.current?.focus();
          searchInputRef.current?.select();
        }
      }
    };
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  // Debounced server search notification
  const onServerSearchChangeRef = useRef(onServerSearchChange);
  onServerSearchChangeRef.current = onServerSearchChange;
  const onServerSortChangeRef = useRef(onServerSortChange);
  onServerSortChangeRef.current = onServerSortChange;
  const onServerFilterChangeRef = useRef(onServerFilterChange);
  onServerFilterChangeRef.current = onServerFilterChange;

  const isFirstSearchMount = useRef(true);
  useEffect(() => {
    if (isFirstSearchMount.current) {
      isFirstSearchMount.current = false;
      return;
    }
    if (!onServerSearchChangeRef.current) return;
    const timer = setTimeout(() => {
      onServerSearchChangeRef.current?.(globalFilter.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [globalFilter]);

  const handleFilterRulesChange = (newRules: FilterRule[]) => {
    setActiveFilterRules(newRules);
    if (resolvedPersistenceKey) {
      saveGridSettings(resolvedPersistenceKey, { filterRules: newRules });
    }
    if (onServerFilterChangeRef.current) {
      onServerFilterChangeRef.current(newRules);
    }
  };

  const handleSortingChange = (updaterOrValue: any) => {
    setSorting((prev) => {
      const next: SortingState =
        typeof updaterOrValue === 'function' ? updaterOrValue(prev) : updaterOrValue;
      if (resolvedPersistenceKey) {
        saveGridSettings(resolvedPersistenceKey, {
          sortRules: next,
          sortCol: next.length > 0 ? next[0].id : '',
          sortDir: next.length > 0 ? (next[0].desc ? 'desc' : 'asc') : undefined,
        });
      }
      if (onServerSortChangeRef.current) {
        if (next && next.length > 0) {
          onServerSortChangeRef.current(next[0].id, next[0].desc ? 'desc' : 'asc', next);
        } else {
          onServerSortChangeRef.current('', '', []);
        }
      }
      return next;
    });
  };

  const handleColumnFilterChange = (colKey: string, newColRules: FilterRule[]) => {
    setActiveFilterRules((prev) => {
      const otherRules = prev.filter((r) => r.columnKey !== colKey);
      const nextRules = [...otherRules, ...newColRules];
      if (resolvedPersistenceKey) {
        saveGridSettings(resolvedPersistenceKey, { filterRules: nextRules });
      }
      if (onServerFilterChangeRef.current) {
        onServerFilterChangeRef.current(nextRules);
      }
      return nextRules;
    });
  };

  const handleColumnSort = (colId: string, direction: 'asc' | 'desc' | false) => {
    handleSortingChange((prev: SortingState) => {
      if (direction === false) {
        return prev.filter((s) => s.id !== colId);
      }
      const others = prev.filter((s) => s.id !== colId);
      return [{ id: colId, desc: direction === 'desc' }, ...others];
    });
  };

  // Active open column filter popup
  const [openFilterColId, setOpenFilterColId] = useState<string | null>(null);

  // Selection state
  const [localRowSelection, setLocalRowSelection] = useState<RowSelectionState>({});
  const rowSelection = useMemo(() => {
    if (controlledSelectedIds !== undefined) {
      const state: RowSelectionState = {};
      controlledSelectedIds.forEach((id) => {
        state[id] = true;
      });
      return state;
    }
    return localRowSelection;
  }, [controlledSelectedIds, localRowSelection]);

  const handleRowSelectionChange = (updaterOrValue: any) => {
    const newSelection =
      typeof updaterOrValue === 'function' ? updaterOrValue(rowSelection) : updaterOrValue;
    if (onSelectionChange) {
      const ids = Object.keys(newSelection).filter((k) => newSelection[k]);
      onSelectionChange(ids);
    } else {
      setLocalRowSelection(newSelection);
    }
  };

  const selectedRowIds = useMemo(() => {
    return Object.keys(rowSelection).filter((k) => rowSelection[k]);
  }, [rowSelection]);

  // Global Escape key to clear selection
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectedRowIds.length > 0) {
        handleRowSelectionChange({});
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedRowIds.length]);

  // Multi-rule filtering (client-side only for non-server-side tables)
  // MANDATORY INVARIANT 9: When running in server-side mode, NEVER filter data client-side!
  const filteredData = useMemo(() => {
    if (isServerSide) {
      return data;
    }

    const hasFilterRules = activeFilterRules.length > 0;
    const hasSearch = Boolean(globalFilter.trim());
    const shouldClientSearch = hasSearch && !onServerSearchChange;
    const shouldClientFilter = hasFilterRules && !onServerFilterChange;

    if (!shouldClientFilter && !shouldClientSearch) {
      return data;
    }

    let result = data;

    // Apply global text search across text columns if not handled by server
    if (shouldClientSearch) {
      const q = globalFilter.toLowerCase().trim();
      result = result.filter((row) => {
        return columns.some((col) => {
          const cleanKey = col.id.replace(/^data_/, '');
          let val = col.accessorFn
            ? col.accessorFn(row)
            : col.accessorKey
            ? (row as any)[col.accessorKey]
            : (row as any)[col.id];
          if (val === undefined && (row as any).data) {
            val = (row as any).data[cleanKey] ?? (row as any).data[col.id];
          }
          if (val === null || val === undefined) return false;
          return String(val).toLowerCase().includes(q);
        });
      });
    }

    // Apply structured filter rules if not handled by server
    if (shouldClientFilter) {
      result = result.filter((row) => {
        return activeFilterRules.every((rule) => {
          const col = columns.find((c) => c.id === rule.columnKey);
          const cleanKey = rule.columnKey.replace(/^data_/, '');
          let rawVal = col?.accessorFn
            ? col.accessorFn(row)
            : col?.accessorKey
            ? (row as any)[col.accessorKey]
            : (row as any)[rule.columnKey];

          if (rawVal === undefined && (row as any).data) {
            rawVal = (row as any).data[cleanKey] ?? (row as any).data[rule.columnKey];
          }

          if (rule.operator === 'boolean') {
            const isTargetTrue = rule.value !== 'false' && rule.value !== '0';
            if (typeof rawVal === 'boolean') return rawVal === isTargetTrue;
            if (typeof rawVal === 'number') return isTargetTrue ? rawVal === 1 : rawVal === 0;
            if (typeof rawVal === 'string') {
              const trimmed = rawVal.trim().toLowerCase();
              return isTargetTrue
                ? ['true', '1', 'ok', 'yes', 'active', 'delivered', 'sent'].includes(trimmed)
                : ['false', '0', 'no', 'spam', 'failed', 'inactive', 'retrying'].includes(trimmed);
            }
            return Boolean(rawVal) === isTargetTrue;
          }

          if (rule.operator === 'in') {
            const allowed = rule.value.split(',').map((v) => v.trim().toLowerCase()).filter(Boolean);
            if (allowed.length === 0) return true;
            const curVal = rawVal !== null && rawVal !== undefined ? String(rawVal).toLowerCase() : '';
            return allowed.some((a) => {
              if (a === 'sent' || a === 'delivered') return curVal === 'sent' || curVal === 'delivered';
              return curVal === a;
            });
          }

          if (rule.operator === 'is_on' || rule.operator === 'is_before' || rule.operator === 'is_after') {
            if (!rawVal) return false;
            const rowDate = new Date(rawVal);
            if (isNaN(rowDate.getTime())) return false;
            const targetDate = new Date(rule.value);
            if (isNaN(targetDate.getTime())) return false;
            const rowDay = rowDate.toISOString().split('T')[0];
            const targetDay = targetDate.toISOString().split('T')[0];
            if (rule.operator === 'is_on') {
              return rowDay === targetDay;
            }
            if (rule.operator === 'is_before') {
              return rowDay < targetDay;
            }
            if (rule.operator === 'is_after') {
              return rowDay > targetDay;
            }
          }

          const strVal = rawVal !== null && rawVal !== undefined ? String(rawVal).toLowerCase() : '';
          const target = (rule.value || '').toLowerCase();

          switch (rule.operator) {
            case 'contains':
              return strVal.includes(target);
            case 'not_contains':
              return !strVal.includes(target);
            case 'equals':
              if (target === 'sent' || target === 'delivered') {
                return strVal === 'sent' || strVal === 'delivered';
              }
              return strVal === target;
            case 'not_equals':
              if (target === 'sent' || target === 'delivered') {
                return strVal !== 'sent' && strVal !== 'delivered';
              }
              return strVal !== target;
            case 'starts_with':
              return strVal.startsWith(target);
            case 'is_empty':
              return strVal === '' || rawVal === null || rawVal === undefined;
            case 'is_not_empty':
              return strVal !== '' && rawVal !== null && rawVal !== undefined;
            case 'greater_than': {
              const numVal = parseFloat(strVal);
              const numTarget = parseFloat(rule.value);
              return !isNaN(numVal) && !isNaN(numTarget) && numVal > numTarget;
            }
            case 'less_than': {
              const numVal = parseFloat(strVal);
              const numTarget = parseFloat(rule.value);
              return !isNaN(numVal) && !isNaN(numTarget) && numVal < numTarget;
            }
            default:
              return true;
          }
        });
      });
    }

    return result;
  }, [data, columns, globalFilter, activeFilterRules, onServerSearchChange, onServerFilterChange, isServerSide]);

  // Client-side pagination only when running client-side (uncontrolled or local data)
  // MANDATORY INVARIANT 9: When running in server-side mode, return data directly!
  const paginatedData = useMemo(() => {
    if (isServerSide) {
      return data;
    }
    const start = (page - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, data, isServerSide, page, pageSize]);

  const totalRows = controlledTotalRows ?? data.length;

  const selectedRows = useMemo(() => {
    const set = new Set(selectedRowIds);
    return data.filter((row, idx) => set.has(getRowId(row, idx)));
  }, [data, selectedRowIds, getRowId]);

  // Client-side export helper
  const triggerClientDownload = (content: string, filename: string, mime: string) => {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExport = async (format: 'csv' | 'json', targetRows?: T[]) => {
    setIsExporting(true);
    try {
      const rowsToExport = targetRows || filteredData;
      const stamp = new Date().toISOString().slice(0, 10);
      const filename = `${tableName}-${stamp}.${format}`;

      if (targetRows && onExportSelected) {
        await onExportSelected(targetRows, format);
        return;
      }

      if (!targetRows && onExportAll) {
        await onExportAll(format);
        return;
      }

      if (format === 'json') {
        const jsonStr = JSON.stringify(rowsToExport, null, 2);
        triggerClientDownload(jsonStr, filename, 'application/json');
      } else {
        // Build CSV
        const exportCols = columns.filter((c) => !c.isSystem);
        const headers = exportCols.map((c) => (typeof c.header === 'string' ? c.header : c.id));
        const lines = [headers.map((h) => `"${h.replace(/"/g, '""')}"`).join(',')];

        rowsToExport.forEach((row) => {
          const vals = exportCols.map((c) => {
            const v = c.accessorFn ? c.accessorFn(row) : (row as any)[c.accessorKey || c.id];
            if (v === null || v === undefined) return '""';
            const str = typeof v === 'object' ? JSON.stringify(v) : String(v);
            return `"${str.replace(/"/g, '""')}"`;
          });
          lines.push(vals.join(','));
        });

        triggerClientDownload(lines.join('\n'), filename, 'text/csv;charset=utf-8;');
      }
    } finally {
      setIsExporting(false);
      setExportOpen(false);
    }
  };

  return {
    density,
    setDensity: handleDensityChange,
    rowHeightClass,
    isServerSide,
    page,
    pageSize,
    isMobile,
    handlePageChange,
    handlePageSizeChange,
    globalFilter,
    setGlobalFilter,
    activeFilterRules,
    handleFilterRulesChange,
    sorting,
    setSorting,
    columnVisibility,
    setColumnVisibility,
    handleSortingChange,
    handleColumnFilterChange,
    handleColumnSort,
    openFilterColId,
    setOpenFilterColId,
    rowSelection,
    selectedRowIds,
    selectedRows,
    handleRowSelectionChange,
    filteredData,
    paginatedData,
    totalRows,
    exportOpen,
    setExportOpen,
    isExporting,
    exportButtonRef,
    handleExport,
    activeCell,
    setActiveCell,
    editingCell,
    setEditingCell,
    searchInputRef,
  };
}
